import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { DevFlag, DevFlagSelectors } from '@recruitee/dev-flags/data';
import { User } from '@recruitee/http/types';
import { createEntityCollectionStore, memoizeSelector } from '@recruitee/ngrx';

import { EntityStoreKey } from '../../types';
import { UserIdMap } from './user.types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<User>()(
  EntityStoreKey.users,
  {
    customActions: adapter => ({
      references: (state, payload: { data: User[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

const byIds = memoizeSelector((adminIds: number[]) =>
  createSelectorFactory(defaultMemoize)(
    ...(adminIds || []).map(adminId => selectors.byId(adminId)),
    (...result: User[]) => result,
  ),
);

const byId = (id: number | null) => (id ? selectors.byId(id) : () => null);

const usersMainScopeSelector = selectors.entities();
const UserSelectors = {
  ...selectors,
  byIds,
  byId,
  byRoleId: memoizeSelector((props: { roleId: number }) =>
    createSelector(usersMainScopeSelector, (users: User[]) =>
      users?.filter(user => user.roleId === props.roleId),
    ),
  ),
  idNameMap: createSelector(usersMainScopeSelector, users =>
    users.reduce((acc, { id, firstName, lastName, anonymizedAt }) => {
      acc[id] = anonymizedAt ? undefined : `${firstName} ${lastName}`;
      return acc;
    }, <Record<number, string | undefined>>{}),
  ),
  idMap: createSelector(usersMainScopeSelector, users =>
    users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, <UserIdMap>{}),
  ),
  accessToCandidateFeatureEnabled: createSelector(
    DevFlagSelectors.data,
    data => data[DevFlag.AccessToCandidate],
  ),
};

export {
  actions as UserActions,
  UserSelectors,
  reducer as UserReducer,
  namespace as UserNamespace,
};
